import { z } from 'zod';

import { currencySchema } from '../currency';
import { orderAdminSchema } from '../order';
import { ulidSchema } from '../primitives';
import { restaurantAdminSchema, restaurantStatusSchema } from '../restaurant';
import {
  adminCreateVoucherRequestSchema,
  adminPlatformVoucherPathParamsSchema,
  adminRestaurantVoucherPathParamsSchema,
  createVoucherResponseSchema,
  listVouchersResponseSchema,
  patchVoucherEnabledRequestSchema,
  patchVoucherEnabledResponseSchema,
} from '../voucher';

import { cursorListResponseSchema } from './_helpers';

export const adminPayoutRunRequestSchema = z.object({
  reason: z.string().min(10).max(1000),
  bypassMinimum: z.boolean().default(false),
  waiveServiceFee: z.boolean().default(false),
  force: z.boolean().default(false),
});
export type AdminPayoutRunRequest = z.infer<typeof adminPayoutRunRequestSchema>;

export const adminRejectRestaurantRequestSchema = z.object({
  reasonNote: z.string().min(10).max(2000),
});
export type AdminRejectRestaurantRequest = z.infer<typeof adminRejectRestaurantRequestSchema>;

export const adminRestaurantListQuerySchema = z.object({
  status: restaurantStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().min(1).optional(),
});
export type AdminRestaurantListQuery = z.infer<typeof adminRestaurantListQuerySchema>;

export const adminRestaurantListWireQuerySchema = z
  .object({
    status: restaurantStatusSchema.optional(),
    limit: z.coerce.number().int().optional(),
    cursor: z.string().optional(),
  })
  .transform((value) => ({
    status: value.status ?? ('PENDING_REVIEW' as z.infer<typeof restaurantStatusSchema>),
    limit:
      value.limit !== undefined &&
      Number.isFinite(value.limit) &&
      value.limit > 0 &&
      value.limit <= 100
        ? value.limit
        : 25,
    ...(value.cursor !== undefined && value.cursor.trim().length > 0
      ? { cursor: value.cursor.trim() }
      : {}),
  }));

export type AdminRestaurantListWireQuery = z.infer<typeof adminRestaurantListWireQuerySchema>;

export const adminRestaurantListResponseSchema = cursorListResponseSchema(restaurantAdminSchema);
export type AdminRestaurantListResponse = z.infer<typeof adminRestaurantListResponseSchema>;

export const adminDisputeListWireQuerySchema = z
  .object({
    limit: z.coerce.number().int().optional(),
    cursor: z.string().optional(),
  })
  .transform((value) => ({
    limit:
      value.limit !== undefined &&
      Number.isFinite(value.limit) &&
      value.limit > 0 &&
      value.limit <= 100
        ? value.limit
        : 25,
    ...(value.cursor !== undefined && value.cursor.trim().length > 0
      ? { cursor: value.cursor.trim() }
      : {}),
  }));

export type AdminDisputeListWireQuery = z.infer<typeof adminDisputeListWireQuerySchema>;

export const adminDisputeListResponseSchema = cursorListResponseSchema(orderAdminSchema);
export type AdminDisputeListResponse = z.infer<typeof adminDisputeListResponseSchema>;

export const adminFlaggedReviewsWireQuerySchema = adminDisputeListWireQuerySchema;
export type AdminFlaggedReviewsWireQuery = z.infer<typeof adminFlaggedReviewsWireQuerySchema>;

export const payoutRunResponseSchema = z.object({
  payoutId: ulidSchema.nullable(),
  tooRecent: z.boolean(),
  amountMinorUnits: z.string().nullable(),
});
export type PayoutRunResponse = z.infer<typeof payoutRunResponseSchema>;

export const adminRestaurantIdPathParamsSchema = z.object({
  id: ulidSchema,
});
export type AdminRestaurantIdPathParams = z.infer<typeof adminRestaurantIdPathParamsSchema>;

export const adminOrderDisputePathParamsSchema = z.object({
  id: ulidSchema,
});
export type AdminOrderDisputePathParams = z.infer<typeof adminOrderDisputePathParamsSchema>;

export const adminStatsTopRestaurantSchema = z.object({
  restaurantId: ulidSchema,
  name: z.string(),
  revenueSevenDaysMinorUnits: z.string(),
  serviceFeeSevenDaysMinorUnits: z.string(),
});
export type AdminStatsTopRestaurant = z.infer<typeof adminStatsTopRestaurantSchema>;

export const adminStatsResponseSchema = z.object({
  currency: currencySchema,
  ordersLastHour: z.number().int(),
  ordersToday: z.number().int(),
  revenueSevenDaysMinorUnits: z.string(),
  serviceFeeSevenDaysMinorUnits: z.string(),
  revenuePreviousSevenDaysMinorUnits: z.string(),
  serviceFeePreviousSevenDaysMinorUnits: z.string(),
  outstandingPayoutMinorUnits: z.string(),
  topRestaurantsByRevenue: z.array(adminStatsTopRestaurantSchema),
});
export type AdminStatsResponse = z.infer<typeof adminStatsResponseSchema>;

export {
  adminCreateVoucherRequestSchema,
  adminPlatformVoucherPathParamsSchema,
  adminRestaurantVoucherPathParamsSchema,
  createVoucherResponseSchema,
  listVouchersResponseSchema,
  patchVoucherEnabledRequestSchema,
  patchVoucherEnabledResponseSchema,
};

export type {
  AdminCreateVoucherRequest,
  PatchVoucherEnabledRequest,
  PatchVoucherEnabledResponse,
} from '../voucher';
